<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
        <div class="modal-container" @keydown.esc="$emit('close')">
          <!-- Header -->
          <div class="modal-header">
            <div class="modal-icon">{{ workflow.icon }}</div>
            <div class="modal-header-text">
              <h2 class="modal-title">{{ workflow.name }}</h2>
              <div class="modal-meta">
                <span class="modal-category">{{ workflow.category }}</span>

                <span v-if="workflow.schedule" class="modal-schedule">⏰ {{ workflow.schedule }}</span>
                <span v-if="workflow.author" class="modal-author">👤 {{ workflow.author }}</span>
                <span class="modal-stat">👁️ {{ workflow.views || 0 }}</span>
                <span class="modal-stat">📥 {{ localDownloads }}</span>
              </div>
            </div>
            <button class="modal-close" @click="$emit('close')" :title="$t('modal.close')">✕</button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <p class="modal-desc">{{ workflow.description }}</p>

            <!-- Steps -->
            <div class="modal-section">
              <h3 class="section-label">{{ $t('modal.steps_label') }}</h3>
              <div class="steps-list">
                <div
                  class="step-item"
                  :class="{ 'step-code': isCodeRef(step) }"
                  v-for="(step, idx) in workflow.steps"
                  :key="idx"
                >
                  <div class="step-number" :class="{ 'step-number-code': isCodeRef(step) }">{{ isCodeRef(step) ? '🐍' : (idx + 1) }}</div>
                  <div class="step-text">{{ stepText(step) }}</div>
                </div>
              </div>
            </div>

            <!-- Hint Tools -->
            <div class="modal-section" v-if="workflow.hint_tools && workflow.hint_tools.length">
              <h3 class="section-label">{{ $t('modal.tools_label') }}</h3>
              <div class="tools-list">
                <span class="tool-chip" v-for="tool in workflow.hint_tools" :key="tool">{{ tool }}</span>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button
              class="btn-import"
              :class="{ disabled: !isLoggedIn || owned }"
              :disabled="!isLoggedIn || owned"
              @click="handleImport"
            >
              <span class="import-icon">{{ owned ? '✅' : '📥' }}</span>
              {{ owned ? $t('modal.already_owned') : (isLoggedIn ? $t('modal.import_button') : $t('modal.login_required')) }}
            </button>
          </div>

          <!-- Toast -->
          <transition name="toast">
            <div v-if="showToast" class="toast-msg">{{ $t('modal.import_toast') }}</div>
          </transition>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
const API_BASE = window.location.origin

export default {
  name: 'WorkflowModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    workflow: {
      type: Object,
      default: () => ({
        icon: '⚡',
        name: '',
        description: '',
        category: '',
        schedule: '',
        steps: [],
        hint_tools: [],
        views: 0,
        downloads: 0,
      }),
    },
    owned: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'downloaded'],
  data() {
    return {
      showToast: false,
      localDownloads: 0,
      isLoggedIn: !!localStorage.getItem('xoul_token'),
    }
  },
  watch: {
    visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', this.handleEsc)
        this.localDownloads = this.workflow.downloads || 0
        this.isLoggedIn = !!localStorage.getItem('xoul_token')
      } else {
        document.body.style.overflow = ''
        window.removeEventListener('keydown', this.handleEsc)
      }
    },
  },
  methods: {
    handleEsc(e) {
      if (e.key === 'Escape') this.$emit('close')
    },
    async handleImport() {
      if (!this.isLoggedIn) return

      const exportData = {
        name: this.workflow.name,
        description: this.workflow.description,
        prompts: JSON.stringify(this.workflow.steps || []),
        hint_tools: Array.isArray(this.workflow.hint_tools) ? this.workflow.hint_tools.join(',') : (this.workflow.hint_tools || ''),
        schedule: this.workflow.schedule || '',
      }

      // Desktop app (QWebEngineView) → wfimport: URL로 전달
      const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(exportData))))
      window.location.href = 'wfimport:' + b64

      // Record download
      fetch(`${API_BASE}/api/workflows/${this.workflow.id}/download`, { method: 'POST' })
      this.localDownloads += 1
      this.$emit('downloaded', this.workflow.id)
    },
    stepText(step) {
      if (typeof step === 'string') return step
      const content = step.content
      if (typeof content === 'object' && content !== null) {
        return content.ko || content.en || JSON.stringify(content)
      }
      return content || JSON.stringify(step)
    },
    isCodeRef(step) {
      if (typeof step !== 'object' || !step) return false
      if (step.type === 'code_name') return true
      if (step.type === 'code' && typeof step.content === 'string' && !step.content.startsWith('def ')) return true
      return false
    },
  },
  beforeUnmount() {
    document.body.style.overflow = ''
    window.removeEventListener('keydown', this.handleEsc)
  },
}
</script>

<style scoped>
/* ── Overlay ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 24px;
}

/* ── Container ── */
.modal-container {
  background: var(--base);
  border: 1px solid var(--surface2);
  border-radius: var(--radius);
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.5);
  position: relative;
}

/* ── Header ── */
.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 28px 28px 20px;
  border-bottom: 1px solid var(--surface1);
}

.modal-icon {
  font-size: 40px;
  line-height: 1;
  flex-shrink: 0;
}

.modal-header-text {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-size: 22px;
  font-weight: 800;
  color: var(--text);
  margin-bottom: 6px;
}

.modal-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.modal-category {
  font-size: 12px;
  color: var(--lavender);
  background: rgba(144, 160, 208, 0.12);
  padding: 3px 10px;
  border-radius: 6px;
  font-weight: 500;
}

.modal-type {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 600;
}

.modal-type.type-persona {
  color: var(--mauve);
  background: rgba(203, 166, 247, 0.12);
}

.modal-type.type-task {
  color: var(--teal);
  background: rgba(148, 226, 213, 0.12);
}

.modal-schedule {
  font-size: 12px;
  color: var(--green);
  background: rgba(112, 192, 128, 0.1);
  padding: 3px 10px;
  border-radius: 6px;
}

.modal-stat {
  font-size: 12px;
  color: var(--overlay1);
}

.modal-author {
  font-size: 12px;
  color: var(--overlay1);
  background: rgba(127, 132, 156, 0.1);
  padding: 3px 10px;
  border-radius: 6px;
}

.modal-close {
  background: var(--surface0);
  border: 1px solid var(--surface2);
  color: var(--subtext0);
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: all var(--transition);
  flex-shrink: 0;
}

.modal-close:hover {
  background: var(--surface1);
  color: var(--text);
  border-color: var(--overlay1);
}

/* ── Body ── */
.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
}

.modal-desc {
  color: var(--subtext1);
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 28px;
}

.modal-section {
  margin-bottom: 24px;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 14px;
}

/* Steps */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--surface0);
  padding: 14px 18px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--surface1);
  transition: border-color var(--transition);
}

.step-item:hover {
  border-color: var(--surface2);
}

.step-item.step-code {
  border-color: rgba(112, 192, 128, 0.3);
  background: rgba(112, 192, 128, 0.06);
}

.step-item.step-code:hover {
  border-color: rgba(112, 192, 128, 0.5);
}

.step-number {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, var(--blue), var(--sapphire));
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-number-code {
  background: linear-gradient(135deg, #40a02b, #70c080);
  font-size: 14px;
}

.step-text {
  font-size: 14px;
  color: var(--subtext1);
  line-height: 1.6;
  padding-top: 3px;
}

/* Persona Prompt */
.persona-prompt-block {
  background: var(--surface0);
  border: 1px solid var(--surface1);
  border-left: 3px solid var(--mauve);
  border-radius: var(--radius-sm);
  padding: 16px 20px;
}

.persona-prompt-text {
  font-size: 14px;
  color: var(--subtext1);
  line-height: 1.8;
  white-space: pre-wrap;
  font-family: 'Inter', sans-serif;
}

/* Tools */
.tools-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tool-chip {
  display: inline-block;
  background: var(--surface0);
  color: var(--peach);
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid var(--surface1);
  font-family: 'Inter', 'Cascadia Code', monospace;
}

/* ── Footer ── */
.modal-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 28px;
  border-top: 1px solid var(--surface1);
  background: var(--mantle);
}

.btn-import {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: var(--radius-sm);
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--blue), var(--sapphire));
  color: #fff;
  transition: all var(--transition);
  box-shadow: 0 4px 20px rgba(96, 144, 208, 0.3);
}

.btn-import:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(96, 144, 208, 0.4);
}

.btn-import.disabled {
  background: var(--surface1);
  color: var(--subtext0);
  cursor: not-allowed;
  box-shadow: none;
}

.btn-import.disabled:hover {
  transform: none;
  box-shadow: none;
}

.import-icon {
  font-size: 18px;
}

/* ── Toast ── */
.toast-msg {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--green);
  color: var(--crust);
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: var(--radius-sm);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

/* ── Transitions ── */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

@media (max-width: 640px) {
  .modal-container {
    max-height: 90vh;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    flex-direction: column;
    padding: 16px 20px;
  }

  .btn-import {
    width: 100%;
    justify-content: center;
  }
}
</style>
